import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  input,
  output,
  inject,
  signal,
  effect,
  untracked,
} from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { UserRole } from '@core/enums/user-role.enum';
import { UserSignalStore } from '@core/stores/user-signal-store.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { SubSink } from 'subsink';
import { AuthenticationService } from 'src/app/@core/services/rest/authentication.service';
import { UserInterface } from 'src/app/@core/interfaces/user.interface';
import { PresignedImageApiService } from '@core/services/api/presigned-image-api.service';
import { UserApiService } from '@core/services/api/user-api.service';
import { NotificationType, TbsNotificationService } from '@ngx-terabase/ui/notification';
import { FileUploadComponent } from '../../../shared/core-file-upload/file-upload.component';
import {
  TbsFormDirective,
  TbsFormItemComponent,
  TbsFormLabelComponent,
  TbsFormControlComponent,
} from '@ngx-terabase/ui/form';
import { TbsButtonDirective } from '@ngx-terabase/ui/button';
import { TbsIconComponent } from '@ngx-terabase/ui/icon';
import { TbsInputDirective } from '@ngx-terabase/ui/input';
import { TbsOptionComponent, TbsSelectComponent } from '@ngx-terabase/ui/select';

const userRoles: Record<string, { text: string; value: string[] }> = {
  superadmin: {
    text: 'Superadmin',
    value: [UserRole.SuperAdmin],
  },
  company_admin: {
    text: 'Company Admin',
    value: [UserRole.CompanyAdmin, UserRole.CompanyUser],
  },
  company_user: {
    text: 'User',
    value: [UserRole.CompanyUser],
  },
};

@Component({
  selector: 'core-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TbsFormDirective,
    TbsFormItemComponent,
    TbsFormLabelComponent,
    TbsFormControlComponent,
    FileUploadComponent,
    TbsButtonDirective,
    TbsIconComponent,
    TbsInputDirective,
    TbsOptionComponent,
    TbsSelectComponent,
  ],
})
export class UserFormComponent implements OnInit, OnDestroy {
  constructor() {
    // submitForm is an input-as-command: the parent flips it to true to trigger submit
    // (replaces ngOnChanges).
    effect(() => {
      if (this.submitForm()) {
        untracked(() => this.submit());
      }
    });
  }

  private formBuilder = inject(UntypedFormBuilder);
  public userStore = inject(UserSignalStore);
  private userApiService = inject(UserApiService);
  private spinner = inject(NgxSpinnerService);
  private imageService = inject(PresignedImageApiService);
  private authService = inject(AuthenticationService);
  private notification = inject(TbsNotificationService);

  public readonly data = input.required<UserInterface>();
  public readonly submitForm = input(false);
  public readonly context = input('profile');
  public readonly formChanged = output<boolean>();

  public userForm!: UntypedFormGroup;
  public userRole = '';
  public readonly roles = signal<{ text: string; value: string }[]>([]);
  public submitted = false;
  public readonly deleteProfileImage = signal(false);
  public profileBlob?: File | null;
  public readonly profilePicture = signal<string | undefined>(undefined);
  private subs = new SubSink();

  public ngOnInit(): void {
    const data = this.data();
    this.userForm = this.formBuilder.group({
      email: [{ value: data?.email, disabled: true }, Validators.required],
      roles: [data.roles ? this.getUserRoleName(data.roles) : null, Validators.required],
      first_name: [data?.contact_profile?.first_name, Validators.required],
      last_name: [data?.contact_profile?.last_name, Validators.required],
      position: [data?.contact_profile?.position],
      video_introduction: [data?.contact_profile?.video_introduction],
      sales_region: [data?.contact_profile?.sales_region],
      description: data?.contact_profile?.description,
      phone_number: [data?.contact_profile?.phone_number],
      profile_picture: null,
    });

    this.roles.set(this.getUserRoles());
    this.userRole = this.getUserRoleName(data.roles);

    if (data?.contact_profile?.profile_picture?._id) {
      this.imageService.getPresignedUrlImage(data?.contact_profile?.profile_picture?._id).subscribe((res) => {
        this.profilePicture.set(res.url);
        this.userForm.markAsDirty();
        this.deleteProfileImage.set(true);
      });
    }

    this.subs.sink = this.userForm.valueChanges.subscribe(() => {
      if (this.userForm.dirty) {
        this.emitChanged(true);
      }
    });
  }

  public uploadProfilePicture(image: { base64: string; binary: File | null }): void {
    this.emitChanged(true);
    this.profilePicture.set(image.base64);
    this.profileBlob = image.binary;
    this.deleteProfileImage.set(true);
  }

  public deleteUploadedImage(): void {
    this.emitChanged(true);
    this.profileBlob = null;
    this.profilePicture.set(undefined);
    this.deleteProfileImage.set(false);
  }

  public submit(): void {
    if (!this.userForm.invalid) {
      this.userForm.value.first_login_finished = true;
      this.userForm.value.profile_picture = this.profileBlob;
      const data = this.data();
      this.spinner.show(data._id);
      this.subs.sink = this.updateUser(this.userForm.value, data._id).subscribe({
        next: () => {
          this.authService.getCognitoGroups().then((groups) => {
            // if user is updating self, refresh current user subject
            if (this.userStore.user()?._id === this.data()._id) {
              this.authService.refreshCurrentUser(groups);
            }
          });
          this.submitted = true;
          this.subs.sink = this.userForm.valueChanges.subscribe(() => {
            this.submitted = false;
          });
          this.spinner.hide(this.data()._id);
          this.emitChanged(false);
          this.notification.snackBar({
            title: 'Changed',
            message: `You successfully edited user profile!`,
            type: NotificationType.Success,
            duration: 5000,
          });
        },
        error: () => {
          this.notification.snackBar({
            title: 'Error',
            message: `Error ocured while editing user profile, please try again.`,
            type: NotificationType.Error,
            duration: 5000,
          });
        },
      });
    }
  }

  public emitChanged(value: boolean) {
    this.formChanged.emit(value);
  }

  public get firstName(): AbstractControl {
    return this.userForm.get('first_name') as AbstractControl;
  }

  public get lastName(): AbstractControl {
    return this.userForm.get('last_name') as AbstractControl;
  }

  public get email(): AbstractControl {
    return this.userForm.get('email') as AbstractControl;
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private updateUser(userData: { roles: any }, userId: any) {
    const formData = new FormData();
    userData.roles = userRoles[userData.roles].value;

    Object.entries(userData).forEach(([index, value]) => {
      if (index === 'roles') {
        value.forEach((v: any, i: any) => {
          formData.set(`${index}[${i}]`, v);
        });
      } else if (index === 'profile_picture') {
        formData.set(index, value);
      } else {
        formData.set(index, value === null ? '' : value);
      }
    });

    return this.userApiService.updateUser(formData, userId, formData.get('profile_picture') === 'null');
  }

  private getUserRoleName(roles: string[]): string {
    let role = '';

    Object.entries(userRoles).forEach(([index, userRole]) => {
      if (JSON.stringify(userRole.value.sort()) === JSON.stringify(roles.sort())) {
        role = index;
      }
    });

    return role;
  }

  private getUserRoles() {
    return Object.entries(userRoles)
      .filter(([name]) => name !== UserRole.SuperAdmin || this.userStore.isSuperAdmin())
      .map(([name, userRole]) => ({
        text: userRole.text,
        value: name,
      }));
  }
}
